import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Card, CardContent, Chip, Grid, LinearProgress,
  Stack, Tooltip, Typography, FormControl, InputLabel, Select, MenuItem, TextField,
  Dialog, DialogTitle, DialogContent, IconButton, Link, Divider,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import { getGeneric } from '../../common/constants'

// RTO band -> colour (green low risk -> red high risk).
const BAND_COLOR = { RED: 'error', ORANGE: 'warning', YELLOW: 'warning', GREEN: 'success', GREY: 'default' }
const RANGE_OPTIONS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
]
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`)
const num = (v) => (v === null || v === undefined ? 0 : v).toLocaleString('en-IN')
const params = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined))

const KpiCard = ({ label, value, note }) => (
  <Card variant='outlined' sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</Typography>
      <Typography variant='h4' fontWeight={700} sx={{ mt: 0.5 }}>{value}</Typography>
      {note && <Typography variant='caption' color='text.secondary'>{note}</Typography>}
    </CardContent>
  </Card>
)

// One confusion-matrix cell. `tone` drives colour; click sets the outcome filter.
const MatrixCell = ({ tag, count, tone, active, onClick }) => {
  const palette = { good: '#e9f5ec', warn: '#fbf0e1', bad: '#fae9e9' }
  const fg = { good: '#15803d', warn: '#b45309', bad: '#b91c1c' }
  return (
    <Box
      role='button' tabIndex={0} onClick={onClick}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
      sx={{ cursor: 'pointer', borderRadius: 1.5, p: 1.75, minHeight: 92, bgcolor: palette[tone] || '#eef1f4',
        border: '2px solid', borderColor: active ? 'text.primary' : 'transparent', '&:hover': { transform: 'translateY(-1px)' }, transition: 'transform .08s' }}
    >
      <Typography variant='caption' fontWeight={700} sx={{ color: fg[tone], textTransform: 'uppercase' }}>{tag}</Typography>
      <Typography variant='h4' fontWeight={700} sx={{ color: fg[tone] }}>{num(count)}</Typography>
    </Box>
  )
}

const ScoreBar = ({ label, value, color }) => (
  <Box sx={{ mb: 1.25 }}>
    <Stack direction='row' justifyContent='space-between'><Typography variant='body2' color='text.secondary'>{label}</Typography><Typography variant='body2' fontWeight={700}>{pct(value)}</Typography></Stack>
    <LinearProgress variant='determinate' value={Math.max(0, Math.min(100, value || 0))} color={color} sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
  </Box>
)

// One parameter's weightage row: label + share-of-total bar + signed impact.
// Reused inline (dense) in the "Why" cell and full-size in the detail dialog.
// `direction`: raises -> red, lowers -> green, neutral -> grey.
const ContributionRow = ({ c, dense }) => {
  const share = Number(c.weight_pct || 0)
  const barColor = c.direction === 'raises' ? '#e53935' : c.direction === 'lowers' ? '#43a047' : '#9e9e9e'
  return (
    <Box sx={{ mb: dense ? 0.5 : 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'baseline' }}>
        <Typography variant='caption' noWrap sx={{ maxWidth: dense ? 150 : 320 }}>{c.label}</Typography>
        <Typography variant='caption' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
          {share.toFixed(0)}%
          <Box component='span' sx={{ color: 'text.secondary', ml: 0.5 }}>
            ({c.shap_delta > 0 ? '+' : ''}{Number(c.shap_delta).toFixed(2)})
          </Box>
        </Typography>
      </Box>
      <Box sx={{ height: dense ? 4 : 6, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.08)', mt: 0.25 }}>
        <Box sx={{ height: dense ? 4 : 6, borderRadius: 2, width: `${Math.min(share, 100)}%`, bgcolor: barColor }} />
      </Box>
    </Box>
  )
}

/**
 * RTO Risk tab of the NETRA Intelligence dashboard. Predicted RTO band
 * (RED/ORANGE = flagged) vs actual RTO outcome — the model's own precision/recall
 * on resolved orders — plus band distribution and a drill-down.
 * Backed by /api/netra/quality/rto-summary + /rto-shipments.
 */
const RtoRiskDashboard = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [rangeDays, setRangeDays] = useState(30)
  const [merchantId, setMerchantId] = useState('')
  const [rtoBand, setRtoBand] = useState('')
  const [outcome, setOutcome] = useState('') // '', RTO, DELIVERED
  const [summary, setSummary] = useState(null)
  const [rows, setRows] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [gridLoading, setGridLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [detailRow, setDetailRow] = useState(null) // row whose full parameter breakdown is open

  const win = useMemo(() => {
    const to = Date.now()
    return { from: to - rangeDays * 864e5, to }
  }, [rangeDays])
  const baseFilters = useMemo(() => ({ from: win.from, to: win.to, merchantId, rtoBand }), [win, merchantId, rtoBand])

  const loadSummary = useCallback(() => {
    getGeneric('netra/quality/rto-summary', params(baseFilters))
      .then((r) => setSummary(r?.data))
      .catch(() => enqueueSnackbar('Could not load the RTO summary', { variant: 'error' }))
  }, [baseFilters, enqueueSnackbar])

  const loadShipments = useCallback(() => {
    setGridLoading(true)
    getGeneric('netra/quality/rto-shipments', params({ ...baseFilters, outcome, page, size: sizePerPage }))
      .then((r) => { setRows(r?.data?.content || []); setRowCount(r?.data?.totalElements || 0) })
      .catch(() => enqueueSnackbar('Could not load RTO shipments', { variant: 'error' }))
      .finally(() => setGridLoading(false))
  }, [baseFilters, outcome, page, sizePerPage, enqueueSnackbar])

  useEffect(() => { loadSummary() }, [loadSummary])
  useEffect(() => { loadShipments() }, [loadShipments])
  useEffect(() => { setPage(0) }, [rangeDays, merchantId, rtoBand, outcome])

  const columns = useMemo(() => [
    { field: 'awb', headerName: 'AWB / Order', width: 170, renderCell: ({ row }) => (
      <Box>
        {row.awb ? (
          <Typography
            variant='body2' fontFamily='monospace' component='a'
            href={`/order-detail/${row.awb}?id=${row.orderId}`}
            target='_blank' rel='noopener noreferrer'
            onClick={(e) => e.stopPropagation()}
            sx={{ color: '#3783e7', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {row.awb}
          </Typography>
        ) : (
          <Typography variant='body2' fontFamily='monospace'>—</Typography>
        )}
        <Typography variant='caption' color='text.secondary' display='block'>{row.orderRef || row.orderId}</Typography>
      </Box>
    ) },
    { field: 'scoredAddressLine', headerName: 'Scored address', width: 230, renderCell: ({ row }) => (
      <Box sx={{ py: 0.5 }}><Typography variant='body2' sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}>{row.scoredAddressLine || '—'}</Typography>
      <Typography variant='caption' color='text.secondary'>{[row.scoredCity, row.scoredState, row.scoredPincode].filter(Boolean).join(', ')}</Typography></Box>
    ) },
    { field: 'rtoScore', headerName: 'RTO risk', width: 130, renderCell: ({ row }) => (
      <Stack direction='row' spacing={1} alignItems='center'>
        <Typography variant='body2' fontWeight={700} fontFamily='monospace'>{row.rtoScore != null ? Math.round(row.rtoScore) : '—'}</Typography>
        {row.rtoBand && <Chip size='small' color={BAND_COLOR[row.rtoBand] || 'default'} label={row.rtoBand} />}
      </Stack>
    ) },
    { field: 'riskReasons', headerName: 'Why (top contributors)', width: 280, sortable: false, renderCell: ({ row }) => {
      const resp = row.rtoScoreResponse || {}
      const reasons = resp.risk_reasons || []
      const contributions = resp.all_contributions || []
      const hygieneBlock = resp.ml_contribution === 0
      if (!reasons.length && !contributions.length) {
        return <Typography variant='caption' color='text.secondary'>{row.rtoScore != null ? 'No elevated risk factors' : '—'}</Typography>
      }
      // Newly-scored orders carry the full weightage; older rows only have the
      // curated risk_reasons text (fallback view, no dialog).
      if (contributions.length) {
        const top = contributions.slice(0, 5)
        return (
          <Box sx={{ py: 0.5, width: '100%' }}>
            {hygieneBlock && <Typography variant='caption' display='block' fontWeight={700} color='error.main'>Hygiene block — model did not run</Typography>}
            {top.map((c) => <ContributionRow key={c.code} c={c} dense />)}
            {contributions.length > 5 && (
              <Link
                component='button' type='button' variant='caption'
                onClick={(e) => { e.stopPropagation(); setDetailRow(row) }}
                sx={{ mt: 0.25 }}
              >
                View all {contributions.length} parameters →
              </Link>
            )}
          </Box>
        )
      }
      return (
        <Tooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              {hygieneBlock && <Typography variant='caption' display='block' fontWeight={700}>Hygiene block — model did not run</Typography>}
              {reasons.map((r) => (
                <Typography key={r.code} variant='caption' display='block'>
                  • {r.description}{r.shap_delta ? ` (${r.shap_delta > 0 ? '+' : ''}${Number(r.shap_delta).toFixed(2)})` : ''}
                </Typography>
              ))}
            </Box>
          }
        >
          <Box sx={{ py: 0.5 }}>
            {reasons.slice(0, 3).map((r) => (
              <Typography key={r.code} variant='caption' display='block' sx={{ lineHeight: 1.4, color: hygieneBlock ? 'error.main' : 'text.primary' }}>
                • {r.description}
              </Typography>
            ))}
            {reasons.length > 3 && <Typography variant='caption' color='text.secondary'>+{reasons.length - 3} more…</Typography>}
          </Box>
        </Tooltip>
      )
    } },
    { field: 'orderStatus', headerName: 'Actual outcome', width: 160, renderCell: ({ row }) => {
      const isRto = row.orderStatus && String(row.orderStatus).startsWith('RTO')
      const txt = row.orderStatus || (row.hadNdr ? 'In NDR' : 'In transit')
      return <Chip size='small' variant='outlined' color={isRto ? 'error' : row.orderStatus === 'DELIVERED' ? 'success' : 'default'} label={txt} />
    } },
  ], [setDetailRow])

  return (
    <Box>
      {/* filters */}
      <Card variant='outlined' sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size='small'><InputLabel>Date range</InputLabel>
                <Select label='Date range' value={rangeDays} onChange={(e) => setRangeDays(e.target.value)}>
                  {RANGE_OPTIONS.map((o) => <MenuItem key={o.days} value={o.days}>{o.label}</MenuItem>)}
                </Select></FormControl>
            </Grid>
            <Grid item xs={6} md={3}><TextField fullWidth size='small' label='Merchant ID (optional)' value={merchantId} onChange={(e) => setMerchantId(e.target.value.trim())} /></Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size='small'><InputLabel>RTO band</InputLabel>
                <Select label='RTO band' value={rtoBand} onChange={(e) => setRtoBand(e.target.value)}>
                  <MenuItem value=''>All bands</MenuItem>
                  {['RED', 'ORANGE', 'YELLOW', 'GREEN'].map((b) => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                </Select></FormControl>
            </Grid>
            {outcome && <Grid item xs={12} md={3}><Chip label={`Outcome: ${outcome}`} onDelete={() => setOutcome('')} color='primary' variant='outlined' /></Grid>}
          </Grid>
        </CardContent>
      </Card>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}><KpiCard label='Orders RTO-scored' value={num(summary?.totalScored)} note=' ' /></Grid>
        <Grid item xs={6} md={3}><KpiCard label='Outcome decided' value={num(summary?.reconciled)} note={`${num(summary?.pending)} pending${summary?.cancelled ? ` · ${num(summary.cancelled)} cancelled (excluded)` : ''}`} /></Grid>
        <Grid item xs={6} md={3}><KpiCard label='Precision @ flagged' value={pct(summary?.precision)} note='of RED+ORANGE, actually RTO' /></Grid>
        <Grid item xs={6} md={3}><KpiCard label='Recall' value={pct(summary?.recall)} note='of RTOs, were flagged' /></Grid>
      </Grid>

      {/* matrix + scorecard */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={7}>
          <Card variant='outlined' sx={{ height: '100%' }}><CardContent>
            <Typography variant='subtitle1' fontWeight={700}>Predicted vs. actual RTO</Typography>
            <Typography variant='caption' color='text.secondary'>Flagged = RED/ORANGE band. Click a column to filter the table by outcome.</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '90px 1fr 1fr', gap: 1, mt: 2 }}>
              <Box /><Typography variant='caption' align='center' color='text.secondary' fontWeight={700}>DELIVERED</Typography><Typography variant='caption' align='center' color='text.secondary' fontWeight={700}>RTO&apos;d</Typography>
              <Typography variant='caption' color='text.secondary' fontWeight={700} sx={{ alignSelf: 'center' }}>FLAGGED</Typography>
              <MatrixCell tag='Over-flagged' tone='warn' count={summary?.falsePositive} active={outcome === 'DELIVERED'} onClick={() => setOutcome('DELIVERED')} />
              <MatrixCell tag='Caught ✓' tone='good' count={summary?.truePositive} active={outcome === 'RTO'} onClick={() => setOutcome('RTO')} />
              <Typography variant='caption' color='text.secondary' fontWeight={700} sx={{ alignSelf: 'center' }}>SAFE</Typography>
              <MatrixCell tag='Correct ✓' tone='good' count={summary?.trueNegative} active={outcome === 'DELIVERED'} onClick={() => setOutcome('DELIVERED')} />
              <MatrixCell tag='Missed' tone='bad' count={summary?.falseNegative} active={outcome === 'RTO'} onClick={() => setOutcome('RTO')} />
            </Box>
          </CardContent></Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card variant='outlined' sx={{ height: '100%' }}><CardContent>
            <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 2 }}>Model scorecard</Typography>
            <ScoreBar label='Precision — flagged that RTO&apos;d' value={summary?.precision} color='warning' />
            <ScoreBar label='Recall — RTOs that were flagged' value={summary?.recall} color='success' />
            <ScoreBar label='Accuracy — overall agreement' value={summary?.accuracy} color='primary' />
            <Box sx={{ mt: 2 }}>
              <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', letterSpacing: '.04em' }}>Band distribution</Typography>
              <Stack spacing={0.75} sx={{ mt: 1 }}>
                {[['RED', summary?.bandRed, '#d32f2f'], ['ORANGE', summary?.bandOrange, '#ed6c02'], ['YELLOW', summary?.bandYellow, '#f9a825'], ['GREEN', summary?.bandGreen, '#2e7d32']].map(([b, c, col]) => (
                  <Box key={b} role='button' onClick={() => setRtoBand(rtoBand === b ? '' : b)} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: col }} />
                    <Typography variant='body2' sx={{ width: 70, fontWeight: rtoBand === b ? 700 : 400 }}>{b}</Typography>
                    <Typography variant='body2' color='text.secondary' fontFamily='monospace'>{num(c)}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </CardContent></Card>
        </Grid>
      </Grid>

      {/* drill-down */}
      <Card variant='outlined'><CardContent>
        <Typography variant='subtitle1' fontWeight={700} sx={{ mb: 1 }}>Shipments</Typography>
        <Box sx={{ position: 'relative' }}>
          {gridLoading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1 }} />}
          <ProzoDataGrid columns={columns} rows={rows} rowCount={rowCount} page={page} setPage={setPage}
            sizePerPage={sizePerPage} setSizePerPage={setSizePerPage} paginationMode='server'
            getRowId={(r) => r.orderId} getRowHeight={() => 'auto'} />
        </Box>
      </CardContent></Card>

      {summary && summary.totalScored === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
          No RTO-scored orders in this window yet. Scores appear once the RTO backend is deployed and useNetra orders are created + reconciled.
        </Typography>
      )}

      <Dialog open={!!detailRow} onClose={() => setDetailRow(null)} maxWidth='sm' fullWidth>
        {detailRow && (() => {
          const resp = detailRow.rtoScoreResponse || {}
          const contributions = resp.all_contributions || []
          const hygieneBlock = resp.ml_contribution === 0
          return (
            <>
              <DialogTitle sx={{ pr: 6 }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <Typography variant='h6' component='span'>Parameter weightage</Typography>
                  {detailRow.rtoBand && <Chip size='small' color={BAND_COLOR[detailRow.rtoBand] || 'default'} label={detailRow.rtoBand} />}
                  {detailRow.rtoScore != null && <Chip size='small' variant='outlined' label={`Score ${Number(detailRow.rtoScore).toFixed(0)}`} />}
                </Stack>
                <Typography variant='caption' color='text.secondary' display='block' sx={{ mt: 0.5 }}>
                  {detailRow.awb || detailRow.orderRef || detailRow.orderId} · {detailRow.scoredAddressLine || ''}
                </Typography>
                <IconButton onClick={() => setDetailRow(null)} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label='close'>✕</IconButton>
              </DialogTitle>
              <DialogContent dividers>
                {hygieneBlock && (
                  <Typography variant='body2' color='error.main' fontWeight={700} sx={{ mb: 1.5 }}>
                    Hygiene block — the model did not run; the score was forced by a deterministic rule.
                  </Typography>
                )}
                {contributions.length ? contributions.map((c) => <ContributionRow key={c.code} c={c} />) : (
                  <Typography variant='body2' color='text.secondary'>No per-parameter breakdown available for this order.</Typography>
                )}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant='caption' color='text.secondary' display='block'>
                  <b>%</b> = share of the model's total decision movement (|Δ| ÷ Σ|Δ|).
                  <br /><b>(Δ)</b> = raw SHAP contribution in log-odds.
                  <br /><Box component='span' sx={{ color: '#e53935' }}>Red</Box> raises RTO risk · <Box component='span' sx={{ color: '#43a047' }}>green</Box> lowers it.
                </Typography>
              </DialogContent>
            </>
          )
        })()}
      </Dialog>
    </Box>
  )
}

export default RtoRiskDashboard
